import {
  Alerta,
  Botao,
  Cartao,
  EstadoCarregando,
  EstadoErro,
  EstadoSemPermissao,
} from '@baiocchi/ui'
import type { ReactNode } from 'react'

import { ErroApi } from '../api/erros'
import { IconeAtualizar } from './icones'

/**
 * Tradução de um erro de consulta para o estado visual certo.
 *
 * A distinção importa porque as três situações pedem ações diferentes:
 *
 *  - **403** — o papel não permite. Não há nada a tentar de novo; o caminho é
 *    falar com o administrador.
 *  - **404** — inexistente **ou fora do escopo**. A API responde igual nos dois
 *    casos de propósito (RNF-24), e a tela não deve especular qual é.
 *  - **501 / rota ausente** — endpoint previsto no contrato e ainda não
 *    implementado. Dizer isso em vez de "erro inesperado" evita que a operação
 *    abra um chamado de defeito para algo que é cronograma.
 *  - **rede** — vale tentar de novo, e o botão precisa existir.
 */
export function EstadoDeErro({
  erro,
  aoTentarNovamente,
  recurso,
}: {
  erro: unknown
  aoTentarNovamente?: () => void
  /**
   * Nome do endpoint, para o aviso de "ainda não disponível".
   *
   * **Passe isto só quando a rota realmente não existir na API.** Por RNF-24 o `404`
   * também é a resposta para *fora do escopo*, então, em rota publicada, este atalho
   * transforma um recorte de segurança funcionando ("este usuário não é da sua
   * unidade") em "o backend não publicou o endpoint" — e o operador abre chamado
   * para um comportamento correto.
   *
   * O aviso era passado por sete telas, herdado da época do `contratos-pendentes.ts`;
   * seis daqueles endpoints já existem. Hoje sobra **um** consumidor legítimo:
   * `PATCH /clientes/{uuid}`, que continua sendo GAP.
   */
  recurso?: string
}) {
  const acaoRepetir = aoTentarNovamente ? (
    <Botao variante="contorno" onClick={aoTentarNovamente} iconeEsquerda={<IconeAtualizar className="h-4 w-4" />}>
      Tentar novamente
    </Botao>
  ) : undefined

  if (erro instanceof ErroApi) {
    if (erro.semPermissao) return <EstadoSemPermissao />

    if (erro.status === 404 && recurso) return <EstadoIndisponivel recurso={recurso} />

    if (erro.naoEncontrado) {
      return (
        <EstadoErro
          titulo="Registro não encontrado"
          descricao="O registro não existe ou está fora do seu escopo de acesso."
          acao={acaoRepetir}
        />
      )
    }

    return <EstadoErro descricao={erro.message} acao={acaoRepetir} />
  }

  return (
    <EstadoErro
      descricao={erro instanceof Error ? erro.message : undefined}
      acao={acaoRepetir}
    />
  )
}

/**
 * Endpoint do contrato que a API ainda não implementou.
 *
 * A alternativa — tela branca ou "erro inesperado" — faria a operação reportar
 * defeito de frontend para uma pendência de backend. Aqui o nome do endpoint
 * aparece, o que também serve de checklist de integração.
 */
export function EstadoIndisponivel({
  recurso,
  descricao,
}: {
  recurso: string
  descricao?: string
}) {
  return (
    <Cartao className="mx-auto max-w-2xl">
      <Alerta tom="atencao" titulo="Esta área ainda não está disponível">
        <p>
          {descricao ??
            'A tela está pronta e integrada ao contrato, mas o endpoint correspondente ainda não foi publicado pela API.'}
        </p>
        <p className="mt-2 font-mono text-xs text-conteudo-fraco">Endpoint esperado: {recurso}</p>
      </Alerta>
    </Cartao>
  )
}

/** Casca padrão de consulta: carregando → erro → conteúdo. */
export function ResultadoConsulta({
  carregando,
  erro,
  aoTentarNovamente,
  recurso,
  vazio,
  estaVazio,
  children,
}: {
  carregando: boolean
  erro: unknown
  aoTentarNovamente?: () => void
  recurso?: string
  vazio?: ReactNode
  estaVazio?: boolean
  children: ReactNode
}) {
  if (carregando) return <EstadoCarregando />
  if (erro) return <EstadoDeErro erro={erro} aoTentarNovamente={aoTentarNovamente} recurso={recurso} />
  if (estaVazio && vazio) return <>{vazio}</>
  return <>{children}</>
}
